import { Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { ApiLinks } from '@cutting/app.api-links';
import { OrderDetailService } from '@cutting/main/order/order-detail/order-detail.service';
import { UserSettingsService } from '@cutting/main/shared/services/user-settings.service';
import {
  BazisMaterialHelper,
  ComponentsModule,
  CuttedMaterialStatisticDialogComponent,
  CuttingClientOptions,
  CuttingCuttedMaterial,
  CuttingMaterial,
  CuttingPanel,
  CuttingSettings,
  ICustomResponse,
  LegacyDialogsService,
  customResponse,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Observable, Subject, iif, of } from 'rxjs';
import { concatMap, filter, map, takeUntil } from 'rxjs/operators';

import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { FuseDirectivesModule } from '@fuse/directives/directives';
import { CuttingMapsComponent } from './cutting-maps/cutting-maps.component';
import { OffcutListComponent } from './offcut-list/offcut-list.component';
import { UsedClientOffcutsComponent } from './used-client-offcuts/used-client-offcuts.component';

@Component({
  selector: 'cutting-cutting-result',
  templateUrl: './cutting-result.component.html',
  styleUrls: ['./cutting-result.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    AsyncPipe,
    MatDividerModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatTabsModule,
    ComponentsModule,
    UsedClientOffcutsComponent,
    OffcutListComponent,
    CuttingMapsComponent,
    FuseDirectivesModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class CuttingResultComponent implements OnDestroy {
  // HACK: crutch for focus first tab
  @Input() showSubTabs = false;

  @Input() achivedOrder = false;
  @Input() cuttingSettings: CuttingSettings;
  @Input() clientOptions: CuttingClientOptions;

  clientShowCuttingMaps: boolean | null = false;
  commonShowCuttingMaps = false;
  commonShowCuttedMaterialStatistic = false;
  clientShowCuttedMaterialStatistic: boolean | null = false;
  data$: Observable<ICustomResponse<CuttingCuttedMaterial[]>>;
  orderMaterials: CuttingMaterial[] = [];

  action$: Observable<any>;
  unsubscribe$: Subject<void> = new Subject();

  get showCuttedMaterialStatistic(): boolean {
    return this.clientOptions.showCuttedMaterialStatistic === null
      ? this.commonShowCuttedMaterialStatistic
      : this.clientOptions.showCuttedMaterialStatistic;
  }

  get showCuttingMaps(): boolean {
    return this.clientOptions.showCuttingMaps === null
      ? this.cuttingSettings.showCuttingMaps
      : this.clientOptions.showCuttingMaps;
  }
  get linkToPanelsSchema(): Observable<string> {
    return this.orderDetailService.onOrderChanged.pipe(
      filter(o => !_.isNil(o)),
      map(o => ApiLinks.panelSchema(o.id))
    );
  }

  constructor(
    private orderDetailService: OrderDetailService, //
    private dialogs: LegacyDialogsService,
    private userSettingsService: UserSettingsService
  ) {
    this.userSettingsService
      .getPresetSettings()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        res => (this.commonShowCuttedMaterialStatistic = res.componentsAvailability.orderDetail.cuttedMaterialStatistic)
      );
    this.refreshData();
  }

  refreshData(): void {
    this.data$ = this.getData();
  }

  getMaterialName(material: CuttingCuttedMaterial): string {
    return BazisMaterialHelper.formatMaterialNameAndArticle(material.name, material.article);
  }

  getSelectedPanels(selectedCuttedMaterialInMaterialBaseId: number): CuttingPanel[] {
    const mIndex = this.orderMaterials.findIndex(m => m.inMaterialBaseId === selectedCuttedMaterialInMaterialBaseId);
    return mIndex < 0 ? [] : this.orderMaterials[mIndex].panels;
  }

  getData(): Observable<ICustomResponse<CuttingCuttedMaterial[]>> {
    return this.orderDetailService.onEndCutting.pipe(
      concatMap(cr =>
        iif(() => cr[0] === 0, this.orderDetailService.getCuttedMaterials(), of([] as CuttingCuttedMaterial[]))
      ),
      customResponse<CuttingCuttedMaterial[]>()
    );
  }

  canTakeAwayOffcuts(materialInMatBaseId: number, materials: CuttingCuttedMaterial[]): CuttingCuttedMaterial {
    return materials.find(m => m.inMaterialBaseId === materialInMatBaseId);
  }

  openMaterialStatistic(material: CuttingCuttedMaterial): void {
    this.action$ = this.dialogs.component(
      CuttedMaterialStatisticDialogComponent,
      { statistic: material.statistic, boardsStatistic: material.boardsStatistic },
      500
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
